import { withTenderTime } from "../../../context/TenderTimeProvider";
import serverClock from "../../../services/serverClock";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./OrderInfoExpeditor.module.scss";
import { Expeditor, File, Order, User } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";
import Loading from "../../shared/Loading/Loading";
import OrderInfoExpeditorHeader from "./components/OrderInfoExpeditorHeader";
import OrderInfoExpeditorStepper from "./components/OrderInfoExpeditorStepper";
import OrderInfoExpeditorTabs from "./components/OrderInfoExpeditorTabs";
import ActionsTab from "./components/tabs/ActionsTab";
import TenderClosedCard from "./components/TenderClosedCard";
import { useOrderTenderState } from "./utils/useOrderTenderState";
import RouteTab from "./components/tabs/RouteTab";
import ExecutorTab from "./components/tabs/ExecutorTab";
import DocumentsTab from "./components/tabs/DocumentsTab";
import HistoryTab from "./components/tabs/HistoryTab";
import OrdersChatHub from "../../shared/chat/OrdersChatHub";
import { useOrderRealtimeEvents } from "../../../hooks/useOrderRealtimeEvents";
import {
  CHAT_REFRESH_TARGETS,
  ORDER_DETAIL_REFRESH_TARGETS,
} from "../../../services/orderRealtimeEvents";
import { useChatUnreadRefresh } from "../../../hooks/useChatUnreadRefresh";
import { loadOrderMapData } from "../utils/loadOrderMapData";

const normalizeOrderDates = (order) => {
  const normalizedOrder = { ...order };

  if (normalizedOrder.shippingDate) {
    const [date] = normalizedOrder.shippingDate.split("T");
    const timeStart = String(normalizedOrder.shippingHourFrom).padStart(2, "0");
    const timeEnd = String(normalizedOrder.shippingHourTo).padStart(2, "0");
    normalizedOrder.shippingDate = `${date}T${timeStart}:00:00`;
    normalizedOrder.shippingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  if (normalizedOrder.unloadingDate) {
    const [date] = normalizedOrder.unloadingDate.split("T");
    const timeStart = String(normalizedOrder.unloadingHourFrom).padStart(2, "0");
    const timeEnd = String(normalizedOrder.unloadingHourTo).padStart(2, "0");
    normalizedOrder.unloadingDate = `${date}T${timeStart}:00:00`;
    normalizedOrder.unloadingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  return normalizedOrder;
};

const createOrderMapState = (orderId = null, isLoadingMap = false) => ({
  orderId,
  cityFrom: null,
  cityTo: null,
  routeData: null,
  coordinate: [],
  isLoadingMap,
});

const OrderInfoExpeditor = ({
  isOpen,
  setIsOpen,
  orderId,
  companyName,
  onForceUpdate,
}) => {
  const { t } = useTranslation();
  const company = useSelector((state) => state.user.company);
  const userCompanyId = useSelector((state) => state.user.companyId ?? state.user.company?.id);
  const { currentLanguage } = useLanguage();

  const [getOrder, setOrder] = useState(null);
  const [cargoDetails, setCargoDetails] = useState();
  const [recipient, setRecipient] = useState();
  const [bookingDate, setBookingDate] = useState();
  const [statusHistory, setStatusHistory] = useState();
  const [mapState, setMapState] = useState(() => createOrderMapState());
  const [listDocDriver, setListDocDriver] = useState([]);
  const [listSuggestedPrice, setListSuggestedPrice] = useState([]);
  const [listFilesOrder, setListFilesOrder] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("actions");
  const [isOpenChatModal, setIsOpenChatModal] = useState(false);

  useEffect(() => {
    setActiveTab((currentTab) => (currentTab === "executor" ? "actions" : currentTab));
  }, [orderId]);

  const orderRequestIdRef = useRef(0);
  const mapRequestIdRef = useRef(0);
  const dependentRequestIdRef = useRef(0);
  const documentsRequestIdRef = useRef(0);
  const dependentOrderIdRef = useRef(null);
  const {
    previousBids,
    bidsLoaded,
    fetchBids,
    isTenderClosed,
    showBidOnly,
    showOrderContent,
    showLoading,
    showBidsError,
  } = useOrderTenderState({ order: getOrder, orderId, isOpen, isLoading, userCompanyId });
  const {
    refreshUnreadMessages,
    refreshUnreadOnClose,
    scheduleUnreadRefresh,
  } = useChatUnreadRefresh({
    getUnreadMessages: Order.getUnreadMessages,
    orderId,
    unreadMessagesCount,
    setUnreadMessagesCount,
  });

  const fetchDocuments = () => {
    if (!getOrder?.id) return;
    const requestId = ++documentsRequestIdRef.current;
    Promise.all([
      File.getOrderDocsDriverPhotos(getOrder.id).catch(() => ({
        status: 200,
        data: [],
      })),
      File.getFilesByOrder(getOrder.id, 5),
      File.getFilesByOrder(getOrder.id, 7),
      File.getFilesByOrder(getOrder.id, 8),
    ])
      .then(([driverPhotosResponse, r5, r7, r8]) => {
        if (requestId !== documentsRequestIdRef.current) return;
        const driverPhotos =
          driverPhotosResponse?.status === 200 ||
          driverPhotosResponse?.status === 201
            ? driverPhotosResponse?.data || []
            : [];
        setListFilesOrder(
          [
            ...(r5?.data || []),
            ...(r7?.data || []),
            ...(r8?.data || []),
            ...driverPhotos,
          ].reverse(),
        );
      })
      .catch((e) => console.log(e));
  };

  const fetchOrderData = useCallback(async ({ silent = false } = {}) => {
    if (!orderId || !isOpen) return;

    const requestId = ++orderRequestIdRef.current;
    if (!silent) {
      setLoading(true);
    }

    try {
      const { data } = await Order.getOrderById(orderId, currentLanguage);

      if (requestId !== orderRequestIdRef.current) return;

      setOrder(normalizeOrderDates(data));
    } catch (error) {
      if (requestId !== orderRequestIdRef.current) return;

      console.log(error);
      toast.error(t("common.genericError"));
    } finally {
      if (requestId === orderRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentLanguage, isOpen, orderId, t]);

  useOrderRealtimeEvents(({ target, data }) => {
    const isMatchingOrder = data?.orderId != null && String(data.orderId) === String(orderId);

    if (!isOpen || !isMatchingOrder) return;

    if (CHAT_REFRESH_TARGETS.has(target)) {
      refreshUnreadMessages();
      return;
    }

    if (ORDER_DETAIL_REFRESH_TARGETS.has(target)) {
      fetchOrderData({ silent: true });
      fetchBids();
    }
  });

  useEffect(() => {
    documentsRequestIdRef.current += 1;
    const dependentRequestId = dependentRequestIdRef.current + 1;
    dependentRequestIdRef.current = dependentRequestId;
    const isCurrentDependentRequest = () =>
      dependentRequestId === dependentRequestIdRef.current;
    const nextOrderId = getOrder?.id ?? null;
    const isSameOrder =
      dependentOrderIdRef.current !== null &&
      nextOrderId !== null &&
      String(dependentOrderIdRef.current) === String(nextOrderId);

    dependentOrderIdRef.current = nextOrderId;

    if (!isSameOrder) {
      setCargoDetails(undefined);
      setRecipient(undefined);
      setBookingDate(undefined);
      setStatusHistory(undefined);
      setListDocDriver([]);
      setListSuggestedPrice([]);
      setListFilesOrder([]);
      setUnreadMessagesCount(0);
    }

    const mapRequestId = mapRequestIdRef.current + 1;
    mapRequestIdRef.current = mapRequestId;

    if (!getOrder) {
      setMapState(createOrderMapState());
      return () => {
        if (dependentRequestId === dependentRequestIdRef.current) {
          dependentRequestIdRef.current += 1;
        }
      };
    }

    if (!isSameOrder) {
      setMapState(createOrderMapState(nextOrderId, true));
    }

    loadOrderMapData(getOrder)
      .then(({ data, errors }) => {
        if (mapRequestId !== mapRequestIdRef.current) return;

        errors.forEach(({ requestName, error }) => {
          console.error(`Failed to load order map ${requestName}`, error);
        });

        setMapState((currentState) => {
          const canPreserveCurrentState =
            isSameOrder && String(currentState.orderId) === String(nextOrderId);
          const nextState = canPreserveCurrentState
            ? { ...currentState }
            : createOrderMapState(nextOrderId);

          Object.assign(nextState, data, { orderId: nextOrderId, isLoadingMap: false });
          return nextState;
        });
      })
      .catch((error) => {
        if (mapRequestId !== mapRequestIdRef.current) return;

        console.error("Failed to load order map data", error);
        setMapState((currentState) => ({ ...currentState, isLoadingMap: false }));
      });

    Order.getCargo(getOrder.cargoId)
      .then((r) => {
        if (!isCurrentDependentRequest()) return;
        setCargoDetails(r.status === 200 ? r.data : {});
      })
      .catch((e) => console.log(e));

    Order.getRecipient(getOrder.recipientId)
      .then((r) => {
        if (!isCurrentDependentRequest()) return;
        setRecipient(r.status === 200 ? r.data : {});
      })
      .catch((e) => console.log(e));

    User.getHistory(getOrder.id)
      .then((res) => res.data)
      .then((data) => {
        if (!isCurrentDependentRequest()) return;
        setBookingDate(data[3]?.createdAt);
        setStatusHistory(data);
      })
      .catch((e) => console.log(e));

    Order.getUnreadMessages(getOrder.id)
      .then((r) => {
        if (!isCurrentDependentRequest()) return;
        if (r.status === 200) setUnreadMessagesCount(r.data?.count || 0);
      })
      .catch((e) => console.log(e));

    if (getOrder.driverId) {
      File.getDocList(getOrder.driverId)
        .then((r) => {
          if (!isCurrentDependentRequest()) return;
          const filtered = r.data.filter((d) => d.type !== 8);
          const hasTypes = [4, 5, 7].some((t) => filtered.some((d) => d.type === t));
          setListDocDriver(filtered);
          if (!hasTypes && getOrder.vehicleId) {
            File.getDocListVehicle(getOrder.vehicleId)
              .then((vr) => {
                if (!isCurrentDependentRequest()) return;
                setListDocDriver([...filtered, ...vr.data]);
              })
              .catch((e) => console.log(e));
          }
        })
        .catch((e) => console.log(e));
    } else if (getOrder.vehicleId) {
      File.getDocListVehicle(getOrder.vehicleId)
        .then((r) => {
          if (!isCurrentDependentRequest()) return;
          setListDocDriver(r.data);
        })
        .catch((e) => console.log(e));
    } else {
      setListDocDriver([]);
    }

    if (getOrder.companyId === company?.id) {
      Order.getListSuggestedPrice(getOrder.id)
        .then((r) => {
          if (!isCurrentDependentRequest()) return;
          setListSuggestedPrice(r?.data?.rows || []);
        })
        .catch((e) => console.log(e));
    }

    Promise.all([
      File.getOrderDocsDriverPhotos(getOrder.id).catch(() => ({
        status: 200,
        data: [],
      })),
      File.getFilesByOrder(getOrder.id, 5),
      File.getFilesByOrder(getOrder.id, 7),
      File.getFilesByOrder(getOrder.id, 8),
    ])
      .then(([driverPhotosResponse, r5, r7, r8]) => {
        if (!isCurrentDependentRequest()) return;
        const driverPhotos =
          driverPhotosResponse?.status === 200 ||
          driverPhotosResponse?.status === 201
            ? driverPhotosResponse?.data || []
            : [];
        setListFilesOrder(
          [
            ...(r5?.data || []),
            ...(r7?.data || []),
            ...(r8?.data || []),
            ...driverPhotos,
          ].reverse(),
        );
      })
      .catch((e) => console.log(e));

    if (getOrder.companyId !== company?.id) {
      Order.createOrderView(getOrder.id).catch((e) => console.log(e));
    }

    return () => {
      if (mapRequestId === mapRequestIdRef.current) {
        mapRequestIdRef.current += 1;
      }
      if (dependentRequestId === dependentRequestIdRef.current) {
        dependentRequestIdRef.current += 1;
      }
    };
  }, [getOrder]);

  const wasOpenRef = useRef(false);
  const checkedOpenOrderIdRef = useRef(null);

  const tryRejectExpiredTender = useCallback(async (order) => {
    if (!order?.tenderDateEnd) return;
    if (serverClock.getNowMs() == null) await serverClock.sync();
    const nowMs = serverClock.getNowMs();
    if (nowMs == null || new Date(order.tenderDateEnd) >= nowMs) return;
    const getBids = order?.isComplexTender
      ? Expeditor.getComplexOrderBidsByExpeditor
      : Expeditor.getOrderBidsByExpeditor;
    getBids(order.id)
      .then((r) => {
        const currentNowMs = serverClock.getNowMs();
        if (currentNowMs == null || new Date(order.tenderDateEnd) >= currentNowMs) return;
        if ((r?.data || []).length === 0) {
          Expeditor.rejectTender(order.id).catch((e) => console.log(e));
        }
      })
      .catch((e) => console.log(e));
  }, []);

  const checkAndRejectTender = useCallback((order) => {
    if (order && (order.status || "").toLowerCase() === "awaitingoffer") {
      tryRejectExpiredTender(order);
    }
  }, [tryRejectExpiredTender]);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setLoading(false);
      return undefined;
    }

    if (!isOpen) {
      setOrder(null);
      setLoading(false);
      return undefined;
    }

    setUnreadMessagesCount(0);
    setOrder((currentOrder) =>
      String(currentOrder?.id) === String(orderId) ? currentOrder : null,
    );
    fetchOrderData();

    return () => {
      orderRequestIdRef.current += 1;
      mapRequestIdRef.current += 1;
      dependentRequestIdRef.current += 1;
      documentsRequestIdRef.current += 1;
    };
  }, [fetchOrderData, isOpen, orderId]);

  useEffect(() => {
    if (!isOpen) {
      checkedOpenOrderIdRef.current = null;
      return;
    }

    if (!getOrder?.id || String(getOrder.id) !== String(orderId)) {
      checkedOpenOrderIdRef.current = null;
      return;
    }

    const currentOrderId = String(getOrder.id);
    if (checkedOpenOrderIdRef.current === currentOrderId) return;

    checkedOpenOrderIdRef.current = currentOrderId;
    checkAndRejectTender(getOrder);
  }, [checkAndRejectTender, getOrder, isOpen, orderId]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      checkAndRejectTender(getOrder);
    }
    wasOpenRef.current = isOpen;
  }, [checkAndRejectTender, getOrder, isOpen]);

  const executorCount = getOrder?.drivers?.length || 0;
  const documentsCount = listFilesOrder.length;
  const isMapStateForCurrentOrder =
    getOrder?.id != null && String(mapState.orderId) === String(getOrder.id);
  const currentMapState = isMapStateForCurrentOrder
    ? mapState
    : createOrderMapState(getOrder?.id ?? null, Boolean(getOrder));

  const handleOpenChat = () => {
    setIsOpenChatModal(true);
    scheduleUnreadRefresh();
  };

  const handleCloseChat = () => {
    setIsOpenChatModal(false);
    refreshUnreadOnClose();
  };

  return (
    <>
    <div
      className={cn(style.overlay, { [style.overlay_open]: isOpen })}
      onClick={() => setIsOpen(false)}
    />
    <div className={cn(style.wrapper, { [style.open]: isOpen })}>
      <OrderInfoExpeditorHeader
        onRefreshOrder={fetchOrderData}
        item={getOrder}
        setIsOpen={setIsOpen}
        onOpenChat={handleOpenChat}
        unreadCount={unreadMessagesCount}
        onForceUpdate={onForceUpdate}
        isSummaryOnly={!showOrderContent}
      />

      {showLoading && <Loading borderRadius="0" />}

      {showOrderContent && (
        <>
          <OrderInfoExpeditorStepper
            status={getOrder?.status}
            assignmentMethod={getOrder?.assignmentMethod}
            transportationType={getOrder?.transportationType}
          />

          <OrderInfoExpeditorTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            executorCount={executorCount}
            documentsCount={documentsCount}
            isWithoutExecutor={getOrder?.isWithoutExecutor}
          />

          <div className={style.content}>
            {activeTab === "actions" && (
              <ActionsTab
                key={getOrder.id}
                order={getOrder}
                onWagonNumbersSaved={(savedOrderId, wagonNumbers) => {
                  setOrder((previous) => previous?.id === savedOrderId ? { ...previous, wagonNumbers } : previous);
                }}
                cargoDetails={cargoDetails}
                statusHistory={statusHistory || []}
                fetchOrderData={fetchOrderData}
                onForceUpdate={onForceUpdate}
                refreshDocuments={fetchDocuments}
                onClose={() => setIsOpen(false)}
                previousBids={previousBids}
                bidsLoaded={bidsLoaded}
                fetchBids={fetchBids}
                onViewRoute={() => setActiveTab("route")}
              />
            )}
            {activeTab === "route" && (
              <RouteTab
                order={getOrder}
                cargoDetails={cargoDetails}
                companyName={companyName}
                recipient={recipient}
                cityFrom={currentMapState.cityFrom}
                cityTo={currentMapState.cityTo}
                routeData={currentMapState.routeData}
                isLoadingMap={currentMapState.isLoadingMap}
                coordinateTruck={currentMapState.coordinate}
              />
            )}
            {activeTab === "executor" && (
              <ExecutorTab
                order={getOrder}
                fetchOrderData={fetchOrderData}
                onForceUpdate={onForceUpdate}
              />
            )}
            {activeTab === "documents" && (
              <DocumentsTab
                order={getOrder}
                listFilesOrder={listFilesOrder}
                refreshDocuments={fetchDocuments}
              />
            )}
            {activeTab === "history" && <HistoryTab history={statusHistory || []} order={getOrder} />}
          </div>
        </>
      )}
      {showBidsError && (
        <div className={style.content} role="alert">
          {t("common.genericError")}
        </div>
      )}
      {showBidOnly && (
        <div className={style.content}>
          <ActionsTab
            onlyBid
            order={getOrder}
            statusHistory={statusHistory || []}
            previousBids={previousBids}
            bidsLoaded={bidsLoaded}
            fetchBids={fetchBids}
            onForceUpdate={onForceUpdate}
            onClose={() => setIsOpen(false)}
          />
        </div>
      )}
      {isTenderClosed && (
        <div className={style.content}>
          <TenderClosedCard assignmentMethod={getOrder?.assignmentMethod} />
        </div>
      )}
    </div>

    {isOpenChatModal && showOrderContent && (
      <OrdersChatHub
        isOpen={isOpenChatModal}
        orderData={getOrder}
        orderId={getOrder.id}
        orderNumber={getOrder.number}
        onClose={handleCloseChat}
      />
    )}
    </>
  );
};

export default withTenderTime(OrderInfoExpeditor);
